import { inject, Injectable } from '@angular/core';
import { TokenService } from '@app/auth';
import type { KpisDiaModel, LocalizacaoModel, PedidoLiveModel, PedidoProcessadoEvent } from '../../../models/dash/vendas-externas';
import { ApiService, SocketService, SOCKET_TYPE } from '@app/gateway';
import { Subscription } from 'rxjs';
import { toLocalizacao, toPedidoLive, toPedidoProcessado } from './vendas-externas.live';

export interface VendasExternasQueryPayload {
  readonly accounts: string[];
  readonly date: string;
}

export interface VendasExternasLiveHandlers {
  readonly onPedido: (pedido: PedidoLiveModel) => void;
  readonly onPedidoProcessado: (patch: PedidoProcessadoEvent) => void;
  readonly onLocalizacao: (point: LocalizacaoModel) => void;
}

/**
 * Supervisor live board: REST snapshot (D3) + socket rooms (D4).
 */
@Injectable({ providedIn: 'root' })
export class VendasExternasService {
  private readonly apiService: ApiService = inject(ApiService);
  private readonly socketService: SocketService = inject(SocketService);
  private readonly tokenService: TokenService = inject(TokenService);

  public async kpisDia(payload: VendasExternasQueryPayload): Promise<KpisDiaModel> {
    return this.apiService.post<KpisDiaModel>({
      path: 'vendasExternas/kpisDia',
      data: { accounts: payload.accounts, date: payload.date },
    });
  }

  public async pedidosLive(payload: VendasExternasQueryPayload): Promise<PedidoLiveModel[]> {
    const result: { pedidos?: PedidoLiveModel[] } = await this.apiService.post<{ pedidos?: PedidoLiveModel[] }>({
      path: 'vendasExternas/pedidosLive',
      data: { accounts: payload.accounts, date: payload.date },
    });
    return result?.pedidos ?? [];
  }

  /**
   * Current GPS pings (TTL 15 min). Date is accepted for the shared body shape; the API ignores it.
   */
  public async localizacoes(payload: VendasExternasQueryPayload): Promise<LocalizacaoModel[]> {
    const result: { vendedores?: LocalizacaoModel[] } = await this.apiService.post<{ vendedores?: LocalizacaoModel[] }>({
      path: 'vendasExternas/localizacoes',
      data: { accounts: payload.accounts, date: payload.date },
    });
    return result?.vendedores ?? [];
  }

  /**
   * Joins `account:{id}` per selected tenant and fans out D4 events. Cleanup leaves the rooms.
   */
  public subscribeLive(accountIds: readonly string[], handlers: VendasExternasLiveHandlers): () => void {
    const token: string = this.tokenService.token;
    const subscriptions: Subscription[] = [
      this.socketService.on<Record<string, unknown>>(SOCKET_TYPE.VENDAS_EXTERNAS_PEDIDO_NOVO).subscribe((raw: Record<string, unknown>) => {
        const pedido: PedidoLiveModel | undefined = toPedidoLive(raw);
        if (pedido) {
          handlers.onPedido(pedido);
        }
      }),
      this.socketService.on<Record<string, unknown>>(SOCKET_TYPE.VENDAS_EXTERNAS_PEDIDO_PROCESSADO).subscribe((raw: Record<string, unknown>) => {
        const patch: PedidoProcessadoEvent | undefined = toPedidoProcessado(raw);
        if (patch) {
          handlers.onPedidoProcessado(patch);
        }
      }),
      this.socketService.on<Record<string, unknown>>(SOCKET_TYPE.VENDAS_EXTERNAS_LOCALIZACAO).subscribe((raw: Record<string, unknown>) => {
        const point: LocalizacaoModel | undefined = toLocalizacao(raw);
        if (point) {
          handlers.onLocalizacao(point);
        }
      }),
    ];
    for (const accountId of accountIds) {
      this.socketService.emit('join', { accountId, token });
    }
    return () => {
      for (const accountId of accountIds) {
        this.socketService.emit('leave', { accountId, token });
      }
      for (const subscription of subscriptions) {
        subscription.unsubscribe();
      }
    };
  }
}
